import { useEffect, useState } from "react";
import { ISelectItem, Select, TSelectValue } from "../../../../../ui/select";
import InputSQMeter from "../../../../../ui/input/input-sq-meter";
import ItemsYmap from "../../../../../ui/map/itemYmap";
import Loader from "../../../../../ui/loader";
import Button from "../../../../../ui/button";
import { ISelectItemExpended } from "../../../interfaces";
import useComplex from "./hooks/useComplex";
import useStreet from "./hooks/useStreet";
import useBuildingComplex from "./hooks/useBuildingComplex";
import useTypes from "./hooks/useTypes";
import useBuildingSecondary from "./hooks/useBuilding";
import useArea from "./hooks/useArea";
import useGetMarketPrice from "./hooks/useGetMarketPrice";
import useReset from "./hooks/useReset";
import clsx from "clsx";
import ModalSupportDeal from "../../modals/modalSupportDeal";
import { getAddress } from "./helpers";
import { useDataEstimateContext } from "../../../hooks/useModalContex";
import {
  IDataFormEstimate,
  IPrimaryFacilityEstimateResponseWithAddress,
  ISecondaryFacilityEstimateResponseWithAddress,
  TModeEstimate,
} from "../../..";
import InputNumber from "../../../../../ui/input/input-number";
import useSetDataForm from "./hooks/useSetDataForm";
import { EMarketTypes } from "../../../../../../interfaces";
import useOpenModal from "../../../../../ui/modal/hooks/useOpenModal";
import InputSearch from "../../../../../ui/input/input-search";

import styles from "./styles.module.scss";

interface IFormEstimateMarketPrice {
  isSecondarySwitcher: boolean;
  onCallBackData?: (
    data:
      | ISecondaryFacilityEstimateResponseWithAddress
      | IPrimaryFacilityEstimateResponseWithAddress
  ) => void;
  resetData?: () => void;
  mode?: TModeEstimate;
  onDataFormEstimate?: ((data: IDataFormEstimate) => void) | undefined;
  dataForm?: IDataFormEstimate;
}

const FormEstimateMarketPrice = ({
  isSecondarySwitcher,
  onCallBackData,
  resetData,
  mode = "default",
  onDataFormEstimate,
  dataForm,
}: IFormEstimateMarketPrice) => {
  /// quiz mode

  const [apartmentNumber, setApartmentNumber] = useState<number | null>(null);

  const handleChangeApartmentNumber = (data: string) => {
    setApartmentNumber(Number(data));
  };

  /// modal support ///

  const { offsetY, open, handleChangeModal } = useOpenModal();

  ///---Primary---///

  /// complexes
  const {
    complexes,
    selectedComplex,
    isLoadingComplexes,
    handleSetComplex,
    handleResetComplex,
  } = useComplex(!isSecondarySwitcher);

  /// complexBuildings
  const {
    selectedBuildingPrimary,
    buildingsComplex,
    isLoadingBuildingComplexes,
    handleSetComplexBuilding,
    handleResetBuildingComplex,
  } = useBuildingComplex(selectedComplex);

  ///---Secondary---///

  /// street
  const {
    streets,
    selectedStreet,
    isLoadingStreets,
    handleSetStreet,
    handleResetStreet,
  } = useStreet(isSecondarySwitcher);

  /// buildingSecondary
  const {
    selectedBuildingSecondary,
    buildingsSecondary,
    errorBuildings,
    isLoadingBuildings,
    handleSetBuilding,
    handleResetBuilding,
  } = useBuildingSecondary(
    (streets as ISelectItem[]).length ? (streets as ISelectItem[]) : [],
    selectedStreet
  );

  ///---Common---//

  /// typesApartment
  const {
    typesApartment,
    selectedTypesApartment,
    minMaxArea,
    isLoadingTypes,
    isLoadingMinMax,
    handleChangeTypesApartment,
    handleResetTypesApartment,
  } = useTypes(selectedBuildingPrimary, selectedBuildingSecondary, mode);

  /// apartmentArea
  const {
    areaInSqM,
    errorAreaInSqM,
    checkValid,
    handleChangeApartmentArea,
    handleResetArea,
  } = useArea(isSecondarySwitcher, minMaxArea);

  /// getMarketPrice
  const {
    onSubmit,
    dataSecondary,
    dataPrimary,
    isMutatingSecondary,
    isMutatingPrimary,
  } = useGetMarketPrice(
    isSecondarySwitcher,
    handleChangeModal,
    resetData,
    mode
  );

  const data = isSecondarySwitcher ? dataSecondary : dataPrimary;
  const isMutating = isSecondarySwitcher
    ? isMutatingSecondary
    : isMutatingPrimary;

  const getMarketPrice = () => {
    onSubmit(
      checkValid,
      selectedBuildingSecondary,
      areaInSqM,
      selectedBuildingPrimary,
      selectedComplex,
      selectedTypesApartment
    );
    if (
      mode !== "default" &&
      selectedBuilding &&
      selectedStreet &&
      areaInSqM &&
      onDataFormEstimate
    ) {
      const dataSent: IDataFormEstimate = {
        streetId: selectedStreet,
        buildingId: selectedBuilding,
        areaInSqM: Number(areaInSqM),
      };

      if (mode === "quiz") {
        dataSent.apartmentNumber = apartmentNumber;
      }

      if (mode === "dealService") {
        dataSent.facilityTypeId = selectedTypesApartment;
      }

      onDataFormEstimate(dataSent);
    }
  };

  const address = getAddress(
    isSecondarySwitcher,
    streets as ISelectItemExpended[],
    buildingsSecondary as ISelectItemExpended[],
    complexes as ISelectItemExpended[],
    buildingsComplex as ISelectItemExpended[],
    selectedStreet,
    selectedBuildingSecondary,
    selectedComplex,
    selectedBuildingPrimary
  );

  useEffect(() => {
    if (data) {
      onCallBackData &&
        onCallBackData({
          ...data,
          address,
        });
    }
  }, [data]);

  /// values
  const streetsOrComplex = isSecondarySwitcher ? streets : complexes;
  const selectedStreetOrComplex = isSecondarySwitcher
    ? selectedStreet
    : selectedComplex;
  const selectedBuilding = isSecondarySwitcher
    ? selectedBuildingSecondary
    : selectedBuildingPrimary;
  const buildings = isSecondarySwitcher ? buildingsSecondary : buildingsComplex;

  const currentCoords = (function () {
    const key = isSecondarySwitcher
      ? selectedBuildingSecondary
      : selectedBuildingPrimary;

    return (buildings as ISelectItemExpended[])?.find(
      (building) => building.key === key
    )?.coordinates;
  })();

  const typeApartment = (function () {
    if (selectedTypesApartment) {
      return (typesApartment as ISelectItem[]).find(
        (type) => type.key === selectedTypesApartment
      )?.label as string;
    }
    return undefined;
  })();

  /// useDataEstimateContext
  const { onSetDataModal } = useDataEstimateContext();

  useEffect(() => {
    if (isSecondarySwitcher) {
      if (areaInSqM && address && selectedBuilding) {
        onSetDataModal({
          address,
          areaInSqM,
          facilityTypeId: selectedTypesApartment ?? undefined,
          buildingId: selectedBuilding,
          streetId: selectedStreet ?? undefined,
          complexId: selectedComplex ?? undefined,
        });
      }
    } else {
      if (areaInSqM && address && selectedBuilding) {
        onSetDataModal({
          address,
          areaInSqM,
          typeApartment,
          facilityTypeId: selectedTypesApartment ?? undefined,
          buildingId: selectedBuilding,
          streetId: selectedStreet ?? undefined,
          complexId: selectedComplex ?? undefined,
        });
      }
    }

    if (mode === "aboutFacilityForm") {
      onSetDataModal({
        areaInSqM: areaInSqM ?? undefined,
        typeApartment: selectedTypesApartment
          ? String(selectedTypesApartment)
          : undefined,
        buildingId: selectedBuilding ?? undefined,
        complexId: selectedComplex ?? undefined,
        streetId: selectedStreet ?? undefined,
        apartmentNumber: apartmentNumber ?? undefined,
      });
    }
  }, [
    apartmentNumber,
    areaInSqM,
    address,
    typeApartment,
    selectedBuilding,
    selectedTypesApartment,
    selectedComplex,
  ]);

  const onSelectStreetsOrComplex = (value: TSelectValue) => {
    if (isSecondarySwitcher) {
      handleSetStreet(value);
    } else {
      handleSetComplex(value);
    }
  };

  const onSelectBuilding = (value: TSelectValue) => {
    if (isSecondarySwitcher) {
      handleSetBuilding(value);
    } else {
      handleSetComplexBuilding(value);
    }
  };

  /// reset
  const { onAllReset, handleIsReset, onResetDataChoice } = useReset(
    isSecondarySwitcher,
    {
      handleResetStreet,
      handleResetBuilding,
      handleResetComplex,
      handleResetBuildingComplex,
      handleResetTypesApartment,
      handleResetArea,
      resetData,
    },
    selectedStreet,
    selectedComplex,
    selectedBuildingSecondary,
    selectedBuildingPrimary,
    selectedTypesApartment,
    areaInSqM
  );

  // setDataForm
  const {} = useSetDataForm(
    dataForm,
    selectedStreetOrComplex,
    streets as ISelectItem[],
    selectedBuilding,
    buildings as ISelectItemExpended[],
    areaInSqM,
    apartmentNumber,
    isLoadingStreets,
    isLoadingBuildings,
    selectedTypesApartment,
    mode,
    onSelectStreetsOrComplex,
    onSelectBuilding,
    handleChangeTypesApartment,
    handleChangeApartmentArea,
    handleChangeApartmentNumber,
    handleIsReset
  );

  // loaders and validators
  const isDisableBuildingSelect = isSecondarySwitcher
    ? !Boolean(selectedStreet)
    : !Boolean(selectedComplex);

  let isAllSelected = Boolean(!isDisableBuildingSelect && areaInSqM);

  if (mode === "quiz") {
    isAllSelected = isAllSelected && Boolean(apartmentNumber);
  }

  const isSecondaryLoading = isLoadingStreets || isLoadingBuildings;
  const isPrimaryLoading = isLoadingComplexes || isLoadingBuildingComplexes;

  const isCurrentLoading = isSecondarySwitcher
    ? isSecondaryLoading
    : isPrimaryLoading;

  const isLoading = isCurrentLoading || isLoadingTypes || isLoadingMinMax;

  return (
    <div className={styles.form}>
      <div
        className={clsx(
          styles.form__container,
          isLoading && styles.form__container_unclick,
          mode === "aboutFacilityForm" && styles.form__container_withoutMargin
        )}
      >
        <div className={styles.form__loader}>{isLoading && <Loader />}</div>
        <div
          className={clsx(
            styles.form__card,
            isLoading && styles.form__card_loading,
            isSecondarySwitcher && styles.form__card_green,
            window.innerWidth < 600 &&
              (mode === "quiz" ||
                mode === "selection" ||
                mode === "aboutFacilityForm" ||
                mode === "tradeIn" ||
                mode === "dealService") &&
              styles.form__card_none
          )}
        >
          {mode !== "aboutFacilityForm" && (
            <div className={styles.form__title}>
              <h4>
                Заполните и узнайте рыночную стоимость вашей текущей квартиры:
              </h4>
            </div>
          )}

          <form
            className={styles.form__form}
            onSubmit={(e) => e.preventDefault()}
          >
            <InputSearch
              minSignSearch={2}
              key={isSecondarySwitcher + "-input-search"}
              onSelect={onSelectStreetsOrComplex}
              menuArray={
                (streetsOrComplex as ISelectItem[])?.length
                  ? (streetsOrComplex as ISelectItem[])
                  : []
              }
              selected={selectedStreetOrComplex}
              placeholder={isSecondarySwitcher ? "Укажите адрес" : "Укажите ЖК"}
              label={isSecondarySwitcher ? "Улица" : "ЖК"}
              error={!Boolean(selectedStreetOrComplex)}
              helper={`Укажите ${
                isSecondarySwitcher ? "улицу" : "ЖК"
              } из списка`}
              onReset={() => {
                isSecondarySwitcher
                  ? onResetDataChoice(["area", "street", "building"])
                  : onResetDataChoice([
                      "complex",
                      "area",
                      "buildingComplex",
                      "typesApartment",
                    ]);
              }}
            />

            <InputSearch
              onSelect={onSelectBuilding}
              menuArray={
                (buildings as ISelectItemExpended[])?.length
                  ? (buildings as ISelectItemExpended[])
                  : []
              }
              selected={selectedBuilding}
              placeholder={"Укажите дом"}
              label="Дом"
              isMainPlaceholder
              disabled={isDisableBuildingSelect}
              error={!Boolean(selectedBuilding)}
              helper={"Укажите дом из списка"}
              key={
                isSecondarySwitcher + "-input-search-" + selectedStreetOrComplex
              }
              onReset={() => {
                isSecondarySwitcher
                  ? onResetDataChoice(["area", "building"])
                  : onResetDataChoice([
                      "area",
                      "buildingComplex",
                      "typesApartment",
                    ]);
              }}
            />

            {(!isSecondarySwitcher || mode === "dealService") && (
              <Select
                search
                onSelect={handleChangeTypesApartment}
                multiple={false}
                menuArray={typesApartment as ISelectItem[]}
                selected={selectedTypesApartment}
                placeholder={"Укажите тип квартиры"}
                label="Тип квартиры"
                isMainPlaceholder
                disabled={!Boolean(selectedBuilding)}
              />
            )}

            <InputSQMeter
              value={areaInSqM}
              onChange={handleChangeApartmentArea}
              error={errorAreaInSqM}
              placeholder={
                minMaxArea
                  ? `От ${minMaxArea?.min} до ${minMaxArea?.max}`
                  : undefined
              }
              isDisable={
                !Boolean(
                  !isSecondarySwitcher
                    ? selectedTypesApartment
                    : selectedBuilding
                )
              }
            />

            {(mode === "quiz" || mode === "aboutFacilityForm") && (
              <InputNumber
                onChange={handleChangeApartmentNumber}
                value={apartmentNumber ? String(apartmentNumber) : null}
                placeholder="Укажите номер квартиры"
                label="Номер квартиры"
                mode="apartment"
              />
            )}
          </form>

          <div className={styles.form__map}>
            <ItemsYmap
              isDisableZoomControl
              items={
                currentCoords
                  ? [
                      {
                        id: 1,
                        coords: [
                          currentCoords.latitude,
                          currentCoords.longitude,
                        ],
                        selected: true,
                      },
                    ]
                  : []
              }
              zoom={currentCoords ? 15 : 11}
              isSetCenterByFirstItem={currentCoords ? true : false}
            />
          </div>

          {mode !== "aboutFacilityForm" && (
            <div className={styles.form__button}>
              <Button
                label="Узнать рыночную стоимость"
                onClick={getMarketPrice}
                disabled={!Boolean(isAllSelected)}
                loading={isMutating}
                variant={isSecondarySwitcher ? "default" : "dark"}
              />
            </div>
          )}
        </div>
      </div>
      {mode === "default" && (
        <ModalSupportDeal
          offsetY={offsetY}
          isOpen={open}
          onAllReset={onAllReset}
          data={{
            areaInSqM: areaInSqM as string,
            typeApartment: typeApartment,
            facilityTypeId: selectedTypesApartment as number,
            address: address,
            buildingId: selectedBuilding as number,
            streetId: selectedStreet as number,
            complexId: selectedComplex as number,
          }}
          onDismiss={(value) => handleChangeModal(value)}
          title={isSecondarySwitcher ? "Заявка на расчет" : "Поддержка"}
          type="support"
          marketType={
            isSecondarySwitcher ? EMarketTypes.SECONDARY : EMarketTypes.PRIMARY
          }
        />
      )}
    </div>
  );
};
export default FormEstimateMarketPrice;
